import React, { useRef, useState, useEffect } from 'react';
import { Eraser, Type, PenTool, Check } from 'lucide-react';

/**
 * SignaturePad Component
 * Allows customers to sign by drawing or typing their name
 * Returns base64 PNG image of signature
 */
const SignaturePad = ({ 
  onSignatureChange, 
  width = 500, 
  height = 200,
  className = '' 
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [mode, setMode] = useState('draw'); // 'draw' or 'type'
  const [typedName, setTypedName] = useState('');
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1e3a5f';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    // Draw signature line
    drawSignatureLine(ctx, canvas.width, canvas.height);
  }, [width, height]);

  const drawSignatureLine = (ctx, canvasWidth, canvasHeight) => {
    ctx.save();
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(20, canvasHeight - 40);
    ctx.lineTo(canvasWidth - 20, canvasHeight - 40);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
    
    // Add "Sign here" text
    ctx.save();
    ctx.fillStyle = '#9ca3af';
    ctx.font = '12px Arial';
    ctx.fillText('Sign above this line', 20, canvasHeight - 20);
    ctx.restore();
  };

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    if (mode !== 'draw') return;
    
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    ctx.strokeStyle = '#1e3a5f';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || mode !== 'draw') return;
    
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawnSignature(true);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      updateSignature();
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    drawSignatureLine(ctx, canvas.width, canvas.height);
    
    setHasDrawnSignature(false);
    setTypedName('');
    onSignatureChange?.(null, null);
  };

  const updateSignature = () => {
    const canvas = canvasRef.current;
    
    if (mode === 'draw' && hasDrawnSignature) {
      const imageData = canvas.toDataURL('image/png');
      onSignatureChange?.(imageData, null);
    } else if (mode === 'type' && typedName.trim()) {
      onSignatureChange?.(null, typedName.trim());
    } else {
      onSignatureChange?.(null, null);
    }
  };

  const handleTypedNameChange = (e) => {
    const name = e.target.value;
    setTypedName(name);
    
    // Render typed signature to canvas
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    drawSignatureLine(ctx, canvas.width, canvas.height);
    
    if (name.trim()) {
      ctx.save();
      ctx.fillStyle = '#1e3a5f';
      ctx.font = 'italic 48px "Brush Script MT", cursive, "Dancing Script", serif';
      ctx.textAlign = 'center';
      ctx.fillText(name, canvas.width / 2, canvas.height - 60);
      ctx.restore();
      
      const imageData = canvas.toDataURL('image/png');
      onSignatureChange?.(imageData, name.trim());
    } else {
      onSignatureChange?.(null, null);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    clearSignature();
  };

  return (
    <div className={`signature-pad ${className}`}>
      {/* Mode Toggle */}
      <div className="flex gap-2 mb-3">
        <button
          type="button"
          onClick={() => switchMode('draw')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            mode === 'draw'
              ? 'bg-blue-100 text-blue-700 border-2 border-blue-300'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <PenTool className="h-4 w-4" />
          Draw Signature
        </button>
        <button
          type="button"
          onClick={() => switchMode('type')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            mode === 'type'
              ? 'bg-blue-100 text-blue-700 border-2 border-blue-300'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Type className="h-4 w-4" />
          Type Name
        </button>
      </div>

      {/* Type input (shown when in type mode) */}
      {mode === 'type' && (
        <div className="mb-3">
          <input
            type="text"
            value={typedName}
            onChange={handleTypedNameChange}
            placeholder="Type your full legal name"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            maxLength={100}
          />
        </div>
      )}

      {/* Canvas */}
      <div className="relative border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          className={`w-full touch-none ${mode === 'draw' ? 'cursor-crosshair' : 'cursor-default'}`}
          style={{ maxWidth: '100%', height: 'auto' }}
        />
        
        {/* Overlay for type mode */}
        {mode === 'type' && !typedName && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-gray-400 text-lg">Your signature will appear here</p>
          </div>
        )}
      </div>

      {/* Clear Button */}
      <div className="mt-3 flex justify-between items-center">
        <button
          type="button"
          onClick={clearSignature}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Eraser className="h-4 w-4" />
          Clear
        </button>
        
        {(hasDrawnSignature || typedName) && (
          <div className="flex items-center gap-2 text-green-600">
            <Check className="h-4 w-4" />
            <span className="text-sm">Signature captured</span>
          </div>
        )}
      </div>

      {mode === 'draw' && (
        <p className="text-xs text-gray-500 mt-2">
          Use your mouse or finger to draw your signature
        </p>
      )}
    </div>
  );
};

export default SignaturePad;